import {
  Box,
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
  VStack,
  HStack,
  Text,
  useDisclosure,
} from '@chakra-ui/core';
import React, { useCallback } from 'react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { QuestionnaireTemplateStore } from '../../store/questionnaireTemplateModel';
import { LANGUAGES } from '../../questionnaireTemplate.constants';
import { LANGUAGE_CODE } from '../../questionnaireTemplate.enums';

export const QuestionSingleLanguageSelector = React.memo(function QuestionSingleLanguageSelector() {
  const { t } = useTranslation('questionnaireTemplate');
  const { isOpen, onToggle, onClose } = useDisclosure();

  const selectedLanguage = QuestionnaireTemplateStore.useStoreState((state) => state.selectedLanguage);
  const isCreatingTemplate = QuestionnaireTemplateStore.useStoreState((s) => s.isCreatingTemplate);
  const hasErrors = QuestionnaireTemplateStore.useStoreState((state) => state.hasErrors);

  const isDisabled = hasErrors || isCreatingTemplate;

  const setSelectedLanguage = QuestionnaireTemplateStore.useStoreActions((actions) => actions.setSelectedLanguage);

  const handleLanguageSelect = useCallback(
    (languageCode: LANGUAGE_CODE) => {
      setSelectedLanguage(languageCode);
      onClose();
    },
    [setSelectedLanguage, onClose],
  );

  return (
    <Popover isOpen={isOpen} onClose={onClose} placement="bottom-start">
      <PopoverTrigger>
        <Button
          size="sm"
          variant="outline"
          onClick={onToggle}
          rightIcon={<ChevronDownIcon />}
          minW="120px"
          justifyContent="space-between"
          isDisabled={isDisabled}
        >
          <Text data-testid="selected-language">
            {t(`formBuilder.elementList.langSelector.languages.${LANGUAGES?.find((l) => l.code === selectedLanguage)?.code}`)}
          </Text>
        </Button>
      </PopoverTrigger>

      <PopoverContent width="240px">
        <PopoverArrow />
        <PopoverBody p={2}>
          <VStack spacing={1} align="stretch">
            <Text fontSize="xs" fontWeight="semibold" color="gray.500" px={2} py={1}>
              {t('formBuilder.elementList.langSelector.title')}
            </Text>
            {LANGUAGES.map((language) => {
              const isSelected = language.code === selectedLanguage;

              return (
                <HStack
                  key={language.code}
                  px={3}
                  py={2}
                  borderRadius="md"
                  cursor="pointer"
                  bg={isSelected ? 'blue.50' : 'transparent'}
                  _hover={{ bg: isSelected ? 'blue.100' : 'gray.50' }}
                  onClick={() => handleLanguageSelect(language.code)}
                  justify="space-between"
                  transition="background 0.2s"
                  data-testid={`language-${language.code}`}
                >
                  <HStack spacing={3} flex={1}>
                    <Box boxSize={2} borderRadius="full" bg={isSelected ? 'blue.500' : 'gray.100'} transition="background 0.2s" />
                    <Text fontWeight={isSelected ? 'semibold' : 'normal'}>
                      {t(`formBuilder.elementList.langSelector.languages.${language.code}`)}
                    </Text>
                  </HStack>
                </HStack>
              );
            })}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
});
